import React, { ReactNode, PropsWithChildren } from 'react'
import * as RadixAccordion from '@radix-ui/react-accordion'
import * as styles from './Accordion.css'
import classNames from 'classnames'
import ArrowDown from '@/icons/navigation/iconArrowDown'
import { IconMinus, IconPlus } from '@/icons'

interface AccordionItemProps {
  title: string
  content: React.ReactNode
  id: string
}

type AccordionTriggerProps = PropsWithChildren<{
  children: ReactNode
  variant?: 'default' | 'gray'
  type?: 'default' | 'guidelines' | 'mobile'
  positionIcon?: 'start' | 'end'
  icon?: React.ReactNode
  onClick?: () => void
}>

export const AccordionTrigger = ({
  children,
  variant = 'default',
  type = 'default',
  positionIcon = 'start',
  icon,
  onClick,
}: AccordionTriggerProps) => {
  const isMobile = type === 'mobile'
  const isGuidelines = type === 'guidelines'
  const headerClass = classNames(styles.AccordionHeader, {
    [styles.AccordionHeaderGuidelines]: isGuidelines,
    [styles.GrayFooter]: variant === 'gray',
  })

  const triggerClass = classNames({
    [styles.AccordionTriggerDefault]: type === 'default',
    [styles.AccordionTriggerGuidelines]: isGuidelines,
    [styles.MobileAccordionTrigger]: isMobile,
    [styles.AccordionTriggerDirection]: positionIcon === 'end',
  })

  const newIcon = icon ? (
    icon
  ) : (
    <div className={classNames('AccordionChevron', styles.Chevron)} aria-hidden>
      <ArrowDown aria-hidden />
    </div>
  )

  return (
    <RadixAccordion.Header className={headerClass}>
      <RadixAccordion.Trigger className={triggerClass} onClick={onClick}>
        {newIcon}
        {children}
      </RadixAccordion.Trigger>
    </RadixAccordion.Header>
  )
}

interface AccordionContentProps {
  children: ReactNode
  className?: string
}

export const AccordionContent = ({
  children,
  className,
}: AccordionContentProps) => {
  return (
    <RadixAccordion.Content
      className={classNames('AccordionContentWrapper', className)}
      forceMount
    >
      <div className={classNames('AccordionContent', styles.AccordionContent)}>
        {children}
      </div>
    </RadixAccordion.Content>
  )
}

interface AccordionProps {
  items: AccordionItemProps[]
  iconOpen?: React.ReactNode
  iconClosed?: React.ReactNode
  clickItemAccordion?: (id: string) => void
}

const Accordion: React.FC<AccordionProps> = ({
  items,
  iconOpen = <IconMinus />,
  iconClosed = <IconPlus />,
  clickItemAccordion,
}) => {
  const icon = (
    <>
      <div
        className={`${styles.AccordionIcon} ${styles.AccordionIconOpen}`}
        aria-hidden
      >
        {iconOpen}
      </div>
      <div
        className={`${styles.AccordionIcon} ${styles.AccordionIconClosed}`}
        aria-hidden
      >
        {iconClosed}
      </div>
    </>
  )
  return (
    <RadixAccordion.Root className={styles.AccordionRoot} type="multiple">
      {items.map((item) => (
        <RadixAccordion.Item
          key={`accordion-${item?.id}`}
          className={styles.AccordionItem}
          value={`item-${item?.id}`}
        >
          <AccordionTrigger
            icon={icon}
            positionIcon="end"
            onClick={() => {
              if (clickItemAccordion) clickItemAccordion(item?.id)
            }}
          >
            <p className={styles.AccordionTitle}>{item?.title}</p>
          </AccordionTrigger>
          <AccordionContent className={styles.AccordionContentText}>
            {item?.content}
          </AccordionContent>
        </RadixAccordion.Item>
      ))}
    </RadixAccordion.Root>
  )
}

export default Accordion
